import { Label } from "components/Label";
import { Content, Horizontal, Vertical } from "gls/lib";
import moment from "moment";
import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import { style } from "typestyle";
import { Error } from "../Error";
import { Calendar } from "./Calendar";
import { isDate } from "lodash";
import { dateTimeFormat } from "./dateTimeFormat";
import { useOutsideAlerter } from "hooks/useOutsideAlerter";
import { Input } from "components/inputs/Input/Input";
import { CalendarIcon } from "./components/CalendarIcon";

type DatePickerProps = {
  value?: Date | string;
  label?: string;
  name?: string;
  minDate?: Date | string;
  maxDate?: Date | string;
  ariaLabel?: string;
  onChange: (date: Date | undefined, errors?: string[]) => void;
  required?: boolean;
  showToday?: boolean;
  openCalender?: boolean;
  onDone?: () => void;
  disabled?: boolean;
  id: string;
  dataTestId?: string;
  helperText?: string;
};

const DEFAULT_PLACE_HOLDER = "Select Date";
export const MAX_DATE_ENTRY = 10;

function matchDate(value: string, regExDefinition: RegExp) {
  return !!value.match(regExDefinition)?.groups;
}

export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (props, ref) => {
    const {
      ariaLabel = "Date Picker",
      disabled,
      name = "date-picker",
      minDate,
      maxDate,
      value,
      dataTestId = props.id,
      label,
      onChange,
      required,
      id,
      onDone,
    } = props;

    const [min, setMin] = useState<Date>();
    const [max, setMax] = useState<Date>();
    const [dateValue, setDateValue] = useState<Date>();
    const [inputValue, setInputValue] = useState("");
    const [isEditingInput, setIsEditingInput] = useState(false);
    const [placeholder, setPlaceholder] = useState(DEFAULT_PLACE_HOLDER);
    const [error, setError] = useState("");
    const [isCalendarOpen, setCalendarOpen] = useState(false);

    const parentRef = useRef<HTMLDivElement>(null);
    const calenderRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const browserLocale = navigator.language;

    const {
      convertToIso1806String,
      regExDefinition,
      shortDateFormat: format
    } = dateTimeFormat(browserLocale);

    useOutsideAlerter(parentRef, () => {
      setCalendarOpen(false);
    });

    useEffect(() => {
      if (value === "" || value === undefined) {
        setDateValue(undefined);
        setInputValue("");
      }
      if (value) {
        const date = isDate(value) ? value : new Date(value);
        setDateValue(date);
        if (!isEditingInput) {
          const m = moment(date);
          setInputValue(m.format(format));
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [format, maxDate, minDate, value]);

    useEffect(() => {
      if (typeof minDate === "string") {
        setMin(minDate === "now" ? new Date() : new Date(minDate));
      } else if (minDate) {
        setMin(minDate);
      } else {
        setMin(undefined);
      }
    }, [minDate]);

    useEffect(() => {
      if (typeof maxDate === "string") {
        setMax(maxDate === "now" ? new Date() : new Date(maxDate));
      } else if (maxDate) {
        setMax(maxDate);
      } else {
        setMax(undefined);
      }
    }, [maxDate]);

    const handleBlur = () => {
      if (isCalendarOpen)
        return;
      handleInputChange(inputValue)
      setPlaceholder(DEFAULT_PLACE_HOLDER)
    }

    const handleInputChange = (changedValue: string) => {
      let value: Date | undefined = undefined;
      let blurError: string = "";
      //nothing to validate
      if (!required && !changedValue) {
        setInputValue(changedValue);
        return setError("");
      }

      if (required && !changedValue) {
        blurError = "This field is required.";
        onChange(undefined, blurError ? [blurError] : undefined);
      } else if (changedValue.length > MAX_DATE_ENTRY && !matchDate(changedValue, regExDefinition)) {
        blurError = !matchDate(changedValue, /\d|\//) ? `Incorrect date format. Please enter date as ${format}` : "";
      } else {
        const date = moment(convertToIso1806String(changedValue), true);

        if (date.isValid()) {
          if (min) {
            const momentMinDate = moment(min).startOf("day");
            if (date.startOf("day").isBefore(momentMinDate)) {
              blurError = `Minimum date is ${momentMinDate
                .format(format)}`;
            }
          }
          if (max && date.isAfter(max)) {
            const momentMaxDate = moment(max);
            // if (date.isAfter(momentMaxDate.add(1, "days"))) {
            blurError = `Maximum date is ${momentMaxDate
              // .subtract(1, "days")
              .format(format)}`;
            // }
          }
          value = date.toDate();
          onChange(value, blurError ? [blurError] : undefined);
        } else {
          blurError = `Incorrect date format. Please enter date as ${format}`;
        }
      }

      !changedValue && setPlaceholder(DEFAULT_PLACE_HOLDER);

      setError(blurError);
      setInputValue(changedValue);
    };

    const handleChange = (value: Date) => {
      if (value) {
        const m = moment(value);
        setInputValue(m.format(format));
        error && setError("");
        onChange(value);
        setCalendarOpen(false);
        onDone && onDone();
      }
    };

    const ContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (e.key === "Escape") {
        setCalendarOpen(false);
      }
      if (e.key === "Tab" && !isCalendarOpen) {
        handleBlur();
      }
    }

    const onInputKeyDown = (e?: React.KeyboardEvent<HTMLInputElement>) => {
      if (e?.key === "Escape") {
        setCalendarOpen(false);
        e.stopPropagation();
      }
      if (e?.key === "Tab" && !isCalendarOpen) {
        handleBlur();
      }
      if (e?.key === "Enter") {
        if (isCalendarOpen) {
          setCalendarOpen(false);
        } else {
          setCalendarOpen(true);
        }
        e.stopPropagation();
      }
    }

    return (
      <Vertical
        horizontalAlign="left"
        spacing={0}
        aria-label={ariaLabel}
        ref={parentRef}
        width="fit-content"
        maxWidth="300px"
      >
        {label && <Label value={label} required={required} />}
        <Vertical spacing={0}>
          <Horizontal verticalAlign="top" onKeyDown={ContainerKeyDown} spacing={5} className={style({ position: 'relative' })}>
            <Input
              id={id}
              name={name}
              value={inputValue}
              placeholder={placeholder}
              onChange={handleInputChange}
              onFocus={() => {
                setPlaceholder(format);
                console.log(value)
                if (value === "" || value === undefined) {
                  setCalendarOpen(true);
                  setIsEditingInput(true);
                  setTimeout(() => {
                    if (calenderRef.current) {
                      calenderRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 0);
                }
              }}
              onBlur={handleBlur}
              onKeyDown={onInputKeyDown}
              onClick={() => setCalendarOpen(true)}
              className={style({
                width: "130px !important",
              })}
              ref={inputRef}
              containerror={!!error}
              ariaLabel={ariaLabel}
              disabled={disabled}
              dataTestId={dataTestId}
            />
            <Content className={style({ marginTop: "15px" })}>
              <CalendarIcon />
            </Content>
            {isCalendarOpen && (
              <span className={style({ display: "contents" })}>
                <Content
                  ref={calenderRef}
                  className={style({
                    position: "absolute",
                    zIndex: 1,
                    inset: '100% auto auto 0px',
                  })}
                >
                  <Calendar
                    value={dateValue}
                    onChange={handleChange}
                    minDate={min}
                    maxDate={max}
                  />
                </Content>
              </span>
            )}
          </Horizontal>
          {error &&
            <Error
              key={`datepicker-err-${id}`}
              id={`baseinput-err-${id}`}
              message={error}
            />
          }
        </Vertical>
      </Vertical>
    );
  }
);

export const DatePickerDefaultProps = {
  label: "Date Label",
  type: "text",
  placeholder: "Some text",
};
